import React, { useState, useEffect } from 'react';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { getFirestore, collection, getDocs, doc, setDoc, deleteDoc, query, where, orderBy, serverTimestamp } from 'firebase/firestore';
import Swal from 'sweetalert2';
import { useSelectedLocation } from './LocationContext';
import SetupGuard from '../components/SetupGuard';
import AdminLayout from './AdminLayout';

const VIEW_MODES = [
  { key: 'week', label: 'Week', icon: 'fa-calendar-week' },
  { key: 'month', label: 'Month', icon: 'fa-calendar-alt' },
  { key: 'list', label: 'List', icon: 'fa-list' },
];

const STATUS_OPTIONS = [
  { key: 'scheduled', label: 'Scheduled', color: '#007bff' },
  { key: 'confirmed', label: 'Confirmed', color: '#28a745' },
  { key: 'completed', label: 'Completed', color: '#6c757d' },
  { key: 'cancelled', label: 'Cancelled', color: '#dc3545' },
];

// Parse "HH:mm" to minutes from midnight
const parseTimeToMinutes = (timeStr) => {
  if (!timeStr) return 0;
  const [h, m] = timeStr.split(':').map(Number);
  return h * 60 + m;
};

const formatDateISO = (date) => {
  const d = new Date(date);
  return d.toISOString().split('T')[0];
};

// Check if two shifts overlap in hours (on the same calendar day)
const shiftsOverlap = (shiftA, shiftB) => {
  let startA = parseTimeToMinutes(shiftA.startHour);
  let endA = parseTimeToMinutes(shiftA.endHour);
  if (shiftA.endsNextDay) endA += 24 * 60;

  let startB = parseTimeToMinutes(shiftB.startHour);
  let endB = parseTimeToMinutes(shiftB.endHour);
  if (shiftB.endsNextDay) endB += 24 * 60;

  return startA < endB && endA > startB;
};

const SchedulePage = ({ embedded = false }) => {
  const [user, setUser] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  // Days with an in-flight write. Tapping a day writes asynchronously and
  // `schedules` state stays STALE until loadSchedules() reloads — without
  // this guard a fast second click saw "not assigned" and created a
  // DUPLICATE shift. Pending days are blocked AND show a spinner so the
  // user immediately sees their click registered.
  const [pendingDays, setPendingDays] = useState(new Set());
  const [viewMode, setViewMode] = useState('week');
  const [currentDate, setCurrentDate] = useState(new Date());
  const { selectedLocation } = useSelectedLocation() || {};
  // List view date range filters (default to current week)
  const getCurrentWeekRange = () => {
    const now = new Date();
    const day = now.getDay();
    const sunday = new Date(now);
    sunday.setDate(now.getDate() - day);
    const saturday = new Date(sunday);
    saturday.setDate(sunday.getDate() + 6);
    return { start: formatDateISO(sunday), end: formatDateISO(saturday) };
  };
  const weekRange = getCurrentWeekRange();
  const [listStartDate, setListStartDate] = useState(weekRange.start);
  const [listEndDate, setListEndDate] = useState(weekRange.end);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('picker'); // 'picker' | 'range' | 'edit'
  const [pickerDate, setPickerDate] = useState(new Date());
  const [editingSchedule, setEditingSchedule] = useState(null);
  const [formData, setFormData] = useState({
    employeeId: '',
    shiftId: '',
    date: '',
    startDate: '',
    endDate: '',
    notes: '',
    status: 'scheduled',
  });

  const auth = getAuth();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        setUser(currentUser);
        await loadData(currentUser.uid);
      }
    });
    return () => unsubscribe();
  }, []);

  // Reload schedules when currentDate or viewMode changes (for week/month navigation)
  useEffect(() => {
    if (!user) return;
    loadSchedules(user.uid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, currentDate, viewMode, listStartDate, listEndDate]);

  const loadData = async (uid) => {
    try {
      const db = getFirestore();

      // Load active employees, optionally filtered by selected location
      let empQuery = query(collection(db, 'users', uid, 'employees'), where('status', '==', 'active'), orderBy('name'));
      const empSnapshot = await getDocs(empQuery);
      let emps = empSnapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      if (selectedLocation?.id) {
        emps = emps.filter(e => e.locationId === selectedLocation.id);
      }
      setEmployees(emps);

      // Load shifts for the selected location
      const shiftSnapshot = await getDocs(
        query(collection(db, 'users', uid, 'shifts'), orderBy('shiftName'))
      );
      let shfts = shiftSnapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      if (selectedLocation?.id) {
        shfts = shfts.filter(s => s.locationId === selectedLocation.id);
      }
      setShifts(shfts);

      await loadSchedules(uid, emps);
    } catch (e) {
      console.error('Error loading schedule data:', e);
    } finally {
      setLoading(false);
    }
  };

  const loadSchedules = async (uid, employeeList = employees) => {
    try {
      const db = getFirestore();
      let start, end;

      if (viewMode === 'week') {
        // Query only the selected week (Sunday to Saturday)
        const d = new Date(currentDate);
        const day = d.getDay();
        const diff = d.getDate() - day;
        const sunday = new Date(d.setDate(diff));
        const saturday = new Date(sunday);
        saturday.setDate(sunday.getDate() + 6);
        start = formatDateISO(sunday);
        end = formatDateISO(saturday);
      } else if (viewMode === 'month') {
        // Query only the selected month
        const startOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
        const endOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
        start = formatDateISO(startOfMonth);
        end = formatDateISO(endOfMonth);
      } else {
        // List view: use the date range filters
        start = listStartDate;
        end = listEndDate;
      }

      const snapshot = await getDocs(
        query(
          collection(db, 'users', uid, 'schedules'),
          where('date', '>=', start),
          where('date', '<=', end),
          orderBy('date', 'asc')
        )
      );
      let scheds = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      // Filter schedules to only show those for employees at the selected location
      if (selectedLocation?.id && employeeList) {
        const employeeIdsAtLocation = new Set(employeeList.filter(e => e.locationId === selectedLocation.id).map(e => e.id));
        scheds = scheds.filter(s => employeeIdsAtLocation.has(s.employeeId));
      }
      setSchedules(scheds);
    } catch (e) {
      console.error('Error loading schedules:', e);
      setSchedules([]);
    }
  };

  // ===================== MODAL OPENERS =====================

  const openNew = () => {
    setEditingSchedule(null);
    const today = formatDateISO(new Date());
    if (viewMode === 'list') {
      setModalMode('range');
      setFormData({ employeeId: '', shiftId: '', date: today, startDate: today, endDate: today, notes: '', status: 'scheduled' });
    } else {
      setModalMode('picker');
      setPickerDate(new Date(currentDate));
      setFormData({ employeeId: '', shiftId: '', date: today, startDate: today, endDate: today, notes: '', status: 'scheduled' });
    }
    setShowModal(true);
  };

  const openEdit = (sched) => {
    setEditingSchedule(sched);
    setModalMode('edit');
    setFormData({
      employeeId: sched.employeeId || '',
      shiftId: sched.shiftId || '',
      date: sched.date || formatDateISO(new Date()),
      startDate: sched.date || formatDateISO(new Date()),
      endDate: sched.date || formatDateISO(new Date()),
      notes: sched.notes || '',
      status: sched.status || 'scheduled',
    });
    setShowModal(true);
  };

  // ===================== PICKER DAY CLICK =====================

  const handlePickerDayClick = async (date) => {
    if (!user) return;
    const iso = formatDateISO(date);

    // Block re-clicks while this day has a write in flight (see pendingDays).
    if (pendingDays.has(iso)) return;

    const empId = formData.employeeId;
    const shiftId = formData.shiftId;

    if (!empId) {
      Swal.fire('Error', 'Please select an employee first', 'error');
      return;
    }
    if (!shiftId) {
      Swal.fire('Error', 'Please select a shift first', 'error');
      return;
    }

    // Mark pending IMMEDIATELY so the cell shows feedback and double-clicks
    // are blocked before the (stale) schedules state catches up.
    setPendingDays(prev => new Set([...prev, iso]));
    try {
      await handlePickerDayClickInner(date, iso);
    } finally {
      setPendingDays(prev => { const next = new Set(prev); next.delete(iso); return next; });
    }
  };

  const handlePickerDayClickInner = async (date, iso) => {
    const empId = formData.employeeId;
    const shiftId = formData.shiftId;

    const emp = employees.find(e => e.id === empId);
    const shift = shifts.find(s => s.id === shiftId);

    // Check if employee already has a schedule on this day
    const existing = schedules.filter(s => s.employeeId === empId && s.date === iso);
    if (existing.length > 0) {
      // Offer to remove the existing assignment(s)
      const result = await Swal.fire({
        title: 'Already Scheduled',
        text: `${emp?.name} is already scheduled on ${iso}. Remove the existing assignment?`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#dc3545',
        cancelButtonColor: '#6c757d',
        confirmButtonText: 'Remove',
        cancelButtonText: 'Keep',
      });
      if (result.isConfirmed) {
        try {
          const db = getFirestore();
          await Promise.all(existing.map(ex => deleteDoc(doc(db, 'users', user.uid, 'schedules', ex.id))));
          Swal.fire({ title: 'Removed', text: 'Assignment removed.', icon: 'success', timer: 1200, showConfirmButton: false });
          await loadSchedules(user.uid);
          window.dispatchEvent(new CustomEvent('setupGuideRefresh'));
        } catch (e) {
          Swal.fire('Error', 'Failed to remove assignment', 'error');
        }
      }
      return;
    }

    // Also check hour conflicts with ANY employee on that day (optional: staffing awareness)
    const daySchedules = schedules.filter(s => s.date === iso);
    const conflicts = daySchedules.filter(s => {
      if (s.employeeId !== empId) return false;
      const existingShift = shifts.find(sh => sh.id === s.shiftId);
      return existingShift && shiftsOverlap(shift, existingShift);
    });
    if (conflicts.length > 0) {
      Swal.fire('Time Conflict', 'This shift overlaps with another assignment for this employee.', 'error');
      return;
    }

    // Create schedule
    try {
      const db = getFirestore();
      const docRef = doc(collection(db, 'users', user.uid, 'schedules'));
      await setDoc(docRef, {
        employeeId: empId,
        employeeName: emp?.name || 'Unknown',
        shiftId: shiftId,
        shiftName: shift?.shiftName || 'Unknown',
        shiftColor: shift?.color || '#007bff',
        date: iso,
        notes: null,
        status: 'scheduled',
        createdAt: serverTimestamp(),
      });
      await loadSchedules(user.uid);
      window.dispatchEvent(new CustomEvent('setupGuideRefresh'));
      Swal.fire({
        title: 'Assigned!',
        text: `${emp?.name} assigned to ${shift?.shiftName} on ${iso}`,
        icon: 'success',
        timer: 1200,
        showConfirmButton: false,
        toast: true,
        position: 'top-end',
      });
    } catch (e) {
      console.error('Error assigning schedule:', e);
      Swal.fire('Error', 'Failed to assign', 'error');
    }
  };

  // ===================== RANGE ASSIGN =====================

  const handleRangeAssign = async () => {
    if (!user) return;
    if (!formData.employeeId) { Swal.fire('Error', 'Please select an employee', 'error'); return; }
    if (!formData.shiftId) { Swal.fire('Error', 'Please select a shift', 'error'); return; }
    if (!formData.startDate || !formData.endDate) { Swal.fire('Error', 'Please select start and end dates', 'error'); return; }

    const emp = employees.find(e => e.id === formData.employeeId);
    const shift = shifts.find(s => s.id === formData.shiftId);

    const start = new Date(formData.startDate);
    const end = new Date(formData.endDate);
    if (start > end) { Swal.fire('Error', 'Start date must be before end date', 'error'); return; }

    const assigned = [];
    const skipped = [];

    try {
      const db = getFirestore();
      for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
        const iso = formatDateISO(d);
        const existing = schedules.filter(s => s.employeeId === formData.employeeId && s.date === iso);
        if (existing.length > 0) {
          skipped.push(iso);
          continue;
        }
        const docRef = doc(collection(db, 'users', user.uid, 'schedules'));
        await setDoc(docRef, {
          employeeId: formData.employeeId,
          employeeName: emp?.name || 'Unknown',
          shiftId: formData.shiftId,
          shiftName: shift?.shiftName || 'Unknown',
          shiftColor: shift?.color || '#007bff',
          date: iso,
          notes: formData.notes.trim() || null,
          status: formData.status,
          createdAt: serverTimestamp(),
        });
        assigned.push(iso);
      }

      await loadSchedules(user.uid);
      Swal.fire({
        title: 'Assignments Complete',
        html: `
          <p><strong>${assigned.length}</strong> day(s) assigned</p>
          ${skipped.length > 0 ? `<p class="text-warning"><strong>${skipped.length}</strong> day(s) skipped (already scheduled)</p>` : ''}
        `,
        icon: 'success',
      });
      setShowModal(false);
      setFormData({ employeeId: '', shiftId: '', date: formatDateISO(new Date()), startDate: formatDateISO(new Date()), endDate: formatDateISO(new Date()), notes: '', status: 'scheduled' });
      window.dispatchEvent(new CustomEvent('setupGuideRefresh'));
    } catch (e) {
      console.error('Error range assigning:', e);
      Swal.fire('Error', e.message || 'Failed to assign', 'error');
    }
  };

  // ===================== EDIT SAVE =====================

  const handleSaveEdit = async () => {
    if (!editingSchedule || !user) return;
    if (!formData.employeeId || !formData.shiftId || !formData.date) {
      Swal.fire('Error', 'Please fill in all required fields', 'error');
      return;
    }

    const emp = employees.find(e => e.id === formData.employeeId);
    const shift = shifts.find(s => s.id === formData.shiftId);

    try {
      const db = getFirestore();
      await setDoc(doc(db, 'users', user.uid, 'schedules', editingSchedule.id), {
        employeeId: formData.employeeId,
        employeeName: emp?.name || 'Unknown',
        shiftId: formData.shiftId,
        shiftName: shift?.shiftName || 'Unknown',
        shiftColor: shift?.color || '#007bff',
        date: formData.date,
        notes: formData.notes.trim() || null,
        status: formData.status,
        updatedAt: serverTimestamp(),
      }, { merge: true });
      Swal.fire('Success', 'Schedule updated!', 'success');
      setShowModal(false);
      setEditingSchedule(null);
      await loadSchedules(user.uid);
      window.dispatchEvent(new CustomEvent('setupGuideRefresh'));
    } catch (e) {
      console.error('Error updating schedule:', e);
      Swal.fire('Error', e.message || 'Failed to update', 'error');
    }
  };

  // ===================== DELETE =====================

  const handleDelete = async (sched) => {
    const result = await Swal.fire({
      title: 'Remove Assignment?',
      text: `Remove ${sched.employeeName}'s ${sched.shiftName} on ${sched.date}?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Remove',
    });
    if (!result.isConfirmed || !user) return;

    try {
      const db = getFirestore();
      await deleteDoc(doc(db, 'users', user.uid, 'schedules', sched.id));
      Swal.fire({ title: 'Removed!', text: 'Assignment removed.', icon: 'success', timer: 1200, showConfirmButton: false });
      await loadSchedules(user.uid);
      window.dispatchEvent(new CustomEvent('setupGuideRefresh'));
    } catch (e) {
      console.error('Error deleting schedule:', e);
      Swal.fire('Error', e.message || 'Failed to remove', 'error');
    }
  };

  // ===================== NAVIGATION =====================

  const goToPrev = () => {
    const d = new Date(currentDate);
    if (viewMode === 'week') d.setDate(d.getDate() - 7);
    else if (viewMode === 'month') d.setMonth(d.getMonth() - 1);
    else d.setDate(d.getDate() - 7);
    setCurrentDate(d);
  };

  const goToNext = () => {
    const d = new Date(currentDate);
    if (viewMode === 'week') d.setDate(d.getDate() + 7);
    else if (viewMode === 'month') d.setMonth(d.getMonth() + 1);
    else d.setDate(d.getDate() + 7);
    setCurrentDate(d);
  };

  const goToToday = () => setCurrentDate(new Date());

  // ===================== DATE HELPERS =====================

  const getWeekDates = () => {
    const d = new Date(currentDate);
    const day = d.getDay();
    const diff = d.getDate() - day;
    const sunday = new Date(d.setDate(diff));
    const dates = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date(sunday);
      date.setDate(sunday.getDate() + i);
      dates.push(date);
    }
    return dates;
  };

  const getMonthDates = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const start = new Date(firstDay);
    start.setDate(start.getDate() - firstDay.getDay());
    const dates = [];
    for (let i = 0; i < 42; i++) {
      const date = new Date(start);
      date.setDate(start.getDate() + i);
      dates.push(date);
    }
    return dates;
  };

  const getSchedulesForDate = (date) => {
    const iso = formatDateISO(date);
    return schedules.filter(s => s.date === iso);
  };

  const formatDateLabel = (date) => {
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const isToday = (date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const isCurrentMonth = (date) => {
    return date.getMonth() === currentDate.getMonth();
  };

  // Picker helpers
  const getPickerWeekDates = () => {
    const d = new Date(pickerDate);
    const day = d.getDay();
    const diff = d.getDate() - day;
    const sunday = new Date(d.setDate(diff));
    const dates = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date(sunday);
      date.setDate(sunday.getDate() + i);
      dates.push(date);
    }
    return dates;
  };

  const getPickerMonthDates = () => {
    const year = pickerDate.getFullYear();
    const month = pickerDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const start = new Date(firstDay);
    start.setDate(start.getDate() - firstDay.getDay());
    const dates = [];
    for (let i = 0; i < 42; i++) {
      const date = new Date(start);
      date.setDate(start.getDate() + i);
      dates.push(date);
    }
    return dates;
  };

  const goPickerPrev = () => {
    const d = new Date(pickerDate);
    if (viewMode === 'week') d.setDate(d.getDate() - 7);
    else d.setMonth(d.getMonth() - 1);
    setPickerDate(d);
  };

  const goPickerNext = () => {
    const d = new Date(pickerDate);
    if (viewMode === 'week') d.setDate(d.getDate() + 7);
    else d.setMonth(d.getMonth() + 1);
    setPickerDate(d);
  };

  const isPickerCurrentMonth = (date) => {
    return date.getMonth() === pickerDate.getMonth();
  };

  // ===================== RENDER =====================

  if (loading) {
    const loader = (
      <div className="text-center py-5">
        <img src="/fodyfee_loading.png" alt="Loading..." style={{ width: 80, height: 80, objectFit: 'contain' }} />
      </div>
    );
    if (embedded) return loader;
    return loader;
  }

  const selectedEmployee = employees.find(e => e.id === formData.employeeId);
  const selectedShift = shifts.find(s => s.id === formData.shiftId);

  const content = (
    <SetupGuard prerequisites={['team', 'shifts']}>
      <>
      <div className="schedule-page">
        {/* Header */}
        <div className="row mb-4 align-items-center">
          <div className="col-md-4">
            <h4 className="font-weight-bold">Schedule</h4>
            <p className="text-muted">
              {selectedLocation?.id ? (
                <>
                  <span className="badge px-2 py-1" style={{ background: '#ff6b3520', color: '#ff6b35', borderRadius: 8 }}>
                    <i className="fas fa-store mr-1"></i>
                    {selectedLocation.companyName || selectedLocation.locationName}
                  </span>
                  <span className="ml-2">Assign shifts to staff</span>
                </>
              ) : (
                'Assign shifts to employees'
              )}
            </p>
          </div>
          <div className="col-md-4 text-center">
            <div className="btn-group">
              <button className="btn btn-outline-secondary btn-sm" onClick={goToPrev}>
                <i className="fas fa-chevron-left"></i>
              </button>
              <button className="btn btn-outline-secondary btn-sm font-weight-bold" onClick={goToToday}>
                Today
              </button>
              <button className="btn btn-outline-secondary btn-sm" onClick={goToNext}>
                <i className="fas fa-chevron-right"></i>
              </button>
            </div>
          </div>
          <div className="col-md-4 text-md-right">
            <button className="btn btn-primary" style={{ backgroundColor: '#ff6b35', borderColor: '#ff6b35' }} onClick={openNew}>
              <i className="fas fa-plus mr-2"></i>Assign Shift
            </button>
          </div>
        </div>

        {/* View Toggle */}
        <div className="d-flex mb-4" style={{ gap: 8 }}>
          {VIEW_MODES.map(v => (
            <button
              key={v.key}
              className={`btn btn-sm ${viewMode === v.key ? 'btn-primary' : 'btn-outline-secondary'}`}
              style={{
                borderRadius: 20,
                backgroundColor: viewMode === v.key ? '#ff6b35' : undefined,
                borderColor: viewMode === v.key ? '#ff6b35' : undefined,
              }}
              onClick={() => setViewMode(v.key)}
            >
              <i className={`fas ${v.icon} mr-1`}></i>{v.label}
            </button>
          ))}
        </div>

        {/* Week View */}
        {viewMode === 'week' && (
          <div className="card" style={{ borderRadius: '16px', border: 'none', boxShadow: '0 2px 10px rgba(0,0,0,0.08)' }}>
            <div className="table-responsive">
              <table className="table table-bordered mb-0" style={{ fontSize: '0.85rem' }}>
                <thead>
                  <tr>
                    {getWeekDates().map((date, idx) => (
                      <th key={idx} className="text-center" style={{
                        backgroundColor: isToday(date) ? '#ff6b3520' : '#f8f9fa',
                        minWidth: 120,
                      }}>
                        <div className="font-weight-bold">{['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][date.getDay()]}</div>
                        <div style={{ color: isToday(date) ? '#ff6b35' : '#6c757d' }}>{formatDateLabel(date)}</div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    {getWeekDates().map((date, idx) => {
                      const daySchedules = getSchedulesForDate(date);
                      return (
                        <td key={idx} className="align-top p-2" style={{
                          height: 200,
                          backgroundColor: isToday(date) ? '#ff6b3508' : undefined,
                          verticalAlign: 'top',
                        }}>
                          {daySchedules.length === 0 ? (
                            <span className="text-muted small">—</span>
                          ) : (
                            daySchedules.map(s => (
                              <div
                                key={s.id}
                                className="mb-2 p-2 position-relative"
                                style={{
                                  borderRadius: 8,
                                  backgroundColor: (s.shiftColor || '#007bff') + '15',
                                  borderLeft: `3px solid ${s.shiftColor || '#007bff'}`,
                                  cursor: 'pointer',
                                  fontSize: '0.8rem',
                                }}
                              >
                                <div onClick={() => openEdit(s)}>
                                  <div className="font-weight-bold">{s.shiftName}</div>
                                  {(() => {
                                    const shiftObj = shifts.find(sh => sh.id === s.shiftId);
                                    if (!shiftObj) return null;
                                    return (
                                      <div className="text-muted" style={{ fontSize: '0.7rem' }}>
                                        {shiftObj.startHour} – {shiftObj.endHour}{shiftObj.endsNextDay ? ' (next day)' : ''}
                                      </div>
                                    );
                                  })()}
                                  <div className="text-muted">{s.employeeName}</div>
                                  {s.status !== 'scheduled' && (
                                    <span className="badge badge-light border mt-1" style={{ fontSize: '0.7rem' }}>
                                      {STATUS_OPTIONS.find(st => st.key === s.status)?.label || s.status}
                                    </span>
                                  )}
                                </div>
                                <button
                                  className="btn btn-sm text-danger position-absolute"
                                  style={{ top: 2, right: 2, padding: '2px 6px', fontSize: '0.7rem' }}
                                  onClick={(e) => { e.stopPropagation(); handleDelete(s); }}
                                  title="Remove"
                                >
                                  <i className="fas fa-times"></i>
                                </button>
                              </div>
                            ))
                          )}
                        </td>
                      );
                    })}
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Month View */}
        {viewMode === 'month' && (
          <div className="card" style={{ borderRadius: '16px', border: 'none', boxShadow: '0 2px 10px rgba(0,0,0,0.08)' }}>
            <div className="card-header bg-light font-weight-bold text-center">
              {currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
            </div>
            <div className="table-responsive">
              <table className="table table-bordered mb-0" style={{ fontSize: '0.8rem', tableLayout: 'fixed', width: '100%' }}>
                <thead>
                  <tr>
                    {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => (
                      <th key={d} className="text-center" style={{ backgroundColor: '#f8f9fa', width: '14.28%' }}>{d}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {Array.from({ length: 6 }).map((_, weekIdx) => (
                    <tr key={weekIdx}>
                      {getMonthDates().slice(weekIdx * 7, (weekIdx + 1) * 7).map((date, dayIdx) => {
                        const daySchedules = getSchedulesForDate(date);
                        return (
                          <td key={dayIdx} className="align-top p-1" style={{
                            height: 100,
                            width: '14.28%',
                            backgroundColor: isToday(date) ? '#ff6b3508' : !isCurrentMonth(date) ? '#f8f9fa' : undefined,
                            opacity: isCurrentMonth(date) ? 1 : 0.6,
                          }}>
                            <div className="text-right mb-1">
                              <span className={isToday(date) ? 'font-weight-bold text-primary' : 'text-muted'} style={{ fontSize: '0.75rem' }}>
                                {date.getDate()}
                              </span>
                            </div>
                            {daySchedules.slice(0, 2).map(s => (
                              <div
                                key={s.id}
                                className="mb-1 px-1 py-1 position-relative"
                                style={{
                                  borderRadius: 4,
                                  backgroundColor: (s.shiftColor || '#007bff') + '20',
                                  fontSize: '0.7rem',
                                  cursor: 'pointer',
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                }}
                                title={`${s.shiftName} — ${s.employeeName}`}
                              >
                                <div onClick={() => openEdit(s)}>
                                  <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                    {s.shiftName}
                                  </div>
                                  <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontWeight: 'normal', opacity: 0.85 }}>
                                    {s.employeeName}
                                  </div>
                                  {(() => {
                                    const shiftObj = shifts.find(sh => sh.id === s.shiftId);
                                    if (!shiftObj) return null;
                                    return (
                                      <div className="text-muted" style={{ fontWeight: 'normal', fontSize: '0.6rem' }}>
                                        {shiftObj.startHour}–{shiftObj.endHour}
                                      </div>
                                    );
                                  })()}
                                </div>
                                <button
                                  className="btn btn-sm text-danger float-right"
                                  style={{ padding: '0 4px', fontSize: '0.6rem', lineHeight: 1 }}
                                  onClick={(e) => { e.stopPropagation(); handleDelete(s); }}
                                  title="Remove"
                                >
                                  <i className="fas fa-times"></i>
                                </button>
                              </div>
                            ))}
                            {daySchedules.length > 2 && (
                              <div className="text-muted" style={{ fontSize: '0.65rem' }}>+{daySchedules.length - 2} more</div>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* List View */}
        {viewMode === 'list' && (
          <>
            {/* Date Range Filters */}
            <div className="card mb-4 p-3" style={{ borderRadius: '16px', border: 'none', boxShadow: '0 2px 10px rgba(0,0,0,0.08)' }}>
              <div className="row align-items-end">
                <div className="col-md-3">
                  <label className="small text-muted mb-1">From</label>
                  <input
                    type="date"
                    className="form-control"
                    value={listStartDate}
                    onChange={e => setListStartDate(e.target.value)}
                  />
                </div>
                <div className="col-md-3">
                  <label className="small text-muted mb-1">To</label>
                  <input
                    type="date"
                    className="form-control"
                    value={listEndDate}
                    onChange={e => setListEndDate(e.target.value)}
                  />
                </div>
                <div className="col-md-3">
                  <button
                    className="btn btn-outline-secondary"
                    style={{ borderRadius: 8 }}
                    onClick={() => {
                      const { start, end } = getCurrentWeekRange();
                      setListStartDate(start);
                      setListEndDate(end);
                    }}
                  >
                    <i className="fas fa-calendar-week mr-1"></i>This Week
                  </button>
                </div>
              </div>
            </div>

            <div className="card" style={{ borderRadius: '16px', border: 'none', boxShadow: '0 2px 10px rgba(0,0,0,0.08)' }}>
              {schedules.length === 0 ? (
                <div className="text-center py-5">
                  <i className="fas fa-calendar fa-4x text-muted mb-4"></i>
                  <h4 className="text-muted">No schedules found</h4>
                  <p className="text-muted">Try adjusting the date range or assign shifts to employees</p>
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-hover mb-0" style={{ fontSize: '0.9rem' }}>
                    <thead className="thead-light">
                      <tr>
                        <th>Date</th>
                        <th>Employee</th>
                        <th>Shift</th>
                        <th>Time</th>
                        <th>Status</th>
                        <th>Notes</th>
                        <th className="text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {schedules.map(s => {
                        const shiftObj = shifts.find(sh => sh.id === s.shiftId);
                        return (
                          <tr key={s.id}>
                            <td className="font-weight-bold">{s.date}</td>
                            <td>{s.employeeName}</td>
                            <td>
                              <span
                                className="badge"
                                style={{
                                  backgroundColor: (s.shiftColor || '#007bff') + '20',
                                  color: s.shiftColor || '#007bff',
                                  fontSize: '0.8rem',
                                  borderRadius: 8,
                                }}
                              >
                                {s.shiftName}
                              </span>
                            </td>
                            <td className="text-muted">
                              {shiftObj ? `${shiftObj.startHour} – ${shiftObj.endHour}` : '—'}
                            </td>
                            <td>
                              <span
                                className="badge"
                                style={{
                                  backgroundColor: (STATUS_OPTIONS.find(st => st.key === s.status)?.color || '#6c757d') + '20',
                                  color: STATUS_OPTIONS.find(st => st.key === s.status)?.color || '#6c757d',
                                  fontSize: '0.8rem',
                                  borderRadius: 8,
                                }}
                              >
                                {STATUS_OPTIONS.find(st => st.key === s.status)?.label || s.status}
                              </span>
                            </td>
                            <td className="text-muted">{s.notes || '—'}</td>
                            <td className="text-right">
                              <button className="btn btn-sm btn-outline-primary mr-1" style={{ borderRadius: 8 }} onClick={() => openEdit(s)}>
                                <i className="fas fa-edit"></i>
                              </button>
                              <button className="btn btn-sm btn-outline-danger" style={{ borderRadius: 8 }} onClick={() => handleDelete(s)}>
                                <i className="fas fa-trash"></i>
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}

        {/* ===================== MODAL ===================== */}
        {showModal && (
          <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className={`modal-dialog modal-dialog-centered ${modalMode === 'picker' ? 'modal-lg' : ''}`}>
              <div className="modal-content" style={{ borderRadius: 16 }}>
                <div className="modal-header border-0">
                  <h5 className="modal-title font-weight-bold">
                    {modalMode === 'picker' ? 'Assign Shift — Tap Days' :
                     modalMode === 'range' ? 'Assign Shift — Date Range' :
                     'Edit Assignment'}
                  </h5>
                  <button type="button" className="close" onClick={() => setShowModal(false)}>
                    <span>&times;</span>
                  </button>
                </div>

                <div className="modal-body">
                  {/* Employee Selection */}
                  <div className="form-group">
                    <label>Employee <span className="text-danger">*</span></label>
                    <select
                      className="form-control"
                      value={formData.employeeId}
                      onChange={e => setFormData({ ...formData, employeeId: e.target.value })}
                    >
                      <option value="">— Select employee —</option>
                      {employees.map(emp => (
                        <option key={emp.id} value={emp.id}>{emp.name}</option>
                      ))}
                    </select>
                  </div>

                  {/* Selected Employee Card */}
                  {selectedEmployee && (
                    <div className="card mb-3" style={{ borderRadius: 12, border: 'none', background: '#fff8f5' }}>
                      <div className="card-body py-2 px-3">
                        <div className="d-flex align-items-center">
                          <div
                            className="d-flex align-items-center justify-content-center mr-3"
                            style={{
                              width: 40,
                              height: 40,
                              borderRadius: '50%',
                              backgroundColor: '#ff6b35',
                              color: '#fff',
                              fontWeight: 'bold',
                              fontSize: '1rem',
                            }}
                          >
                            {selectedEmployee.name?.charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <div className="font-weight-bold">{selectedEmployee.name}</div>
                            <small className="text-muted">{selectedEmployee.role} • {selectedEmployee.email || 'No email'}</small>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Shift Selection */}
                  <div className="form-group">
                    <label>Shift <span className="text-danger">*</span></label>
                    <select
                      className="form-control"
                      value={formData.shiftId}
                      onChange={e => setFormData({ ...formData, shiftId: e.target.value })}
                    >
                      <option value="">— Select shift —</option>
                      {shifts.map(shift => (
                        <option key={shift.id} value={shift.id}>
                          {shift.shiftName} ({shift.startHour} - {shift.endHour}{shift.endsNextDay ? ' next day' : ''})
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* PICKER MODE: Day Grid */}
                  {modalMode === 'picker' && selectedEmployee && selectedShift && (
                    <div className="mt-3">
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <h6 className="font-weight-bold m-0">Tap a day to assign</h6>
                        <div className="btn-group btn-group-sm">
                          <button className="btn btn-outline-secondary" onClick={goPickerPrev}>
                            <i className="fas fa-chevron-left"></i>
                          </button>
                          <button className="btn btn-outline-secondary font-weight-bold" disabled>
                            {pickerDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                          </button>
                          <button className="btn btn-outline-secondary" onClick={goPickerNext}>
                            <i className="fas fa-chevron-right"></i>
                          </button>
                        </div>
                      </div>

                      {/* Week Picker */}
                      {viewMode === 'week' && (
                        <div className="row">
                           {getPickerWeekDates().map((date, idx) => {
                             const iso = formatDateISO(date);
                             const empSchedules = schedules.filter(s => s.employeeId === formData.employeeId && s.date === iso);
                             const allDaySchedules = schedules.filter(s => s.date === iso);
                             const isAssigned = empSchedules.length > 0;
                             const isCurrentDay = isToday(date);
                             const isPending = pendingDays.has(iso);

                             return (
                               <div key={idx} className="col p-1">
                                 <div
                                   className="text-center p-2"
                                   style={{
                                     borderRadius: 12,
                                     border: `2px solid ${isPending ? '#ff6b35' : isAssigned ? (empSchedules[0].shiftColor || '#28a745') : isCurrentDay ? '#ff6b35' : '#e9ecef'}`,
                                     backgroundColor: isPending ? '#ff6b3508' : isAssigned ? (empSchedules[0].shiftColor || '#28a745') + '15' : isCurrentDay ? '#ff6b3508' : '#fff',
                                     cursor: isPending ? 'wait' : 'pointer',
                                     minHeight: 90,
                                     transition: 'all 0.2s',
                                   }}
                                   onClick={() => !isPending && handlePickerDayClick(date)}
                                   onMouseEnter={(e) => { if (!isPending) e.currentTarget.style.transform = 'scale(1.02)'; }}
                                   onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
                                 >
                                   <div className="font-weight-bold" style={{ fontSize: '0.8rem', color: isCurrentDay ? '#ff6b35' : '#333' }}>
                                     {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][date.getDay()]}
                                   </div>
                                   <div style={{ fontSize: '0.9rem', marginBottom: 4 }}>{date.getDate()}</div>
                                   {isPending ? (
                                     <div className="mt-1"><i className="fas fa-spinner fa-spin" style={{ color: '#ff6b35' }}></i></div>
                                   ) : isAssigned ? (
                                     <>
                                       <span
                                         className="badge"
                                         style={{
                                           backgroundColor: (empSchedules[0].shiftColor || '#28a745') + '30',
                                           color: empSchedules[0].shiftColor || '#28a745',
                                           fontSize: '0.7rem',
                                         }}
                                       >
                                         {empSchedules[0].shiftName}
                                       </span>
                                       <div className="text-muted mt-1" style={{ fontSize: '0.65rem' }}>
                                         <i className="fas fa-times-circle mr-1"></i>Tap to remove
                                       </div>
                                     </>
                                   ) : (
                                     <div className="text-muted" style={{ fontSize: '0.7rem' }}>
                                       <i className="fas fa-plus-circle mr-1"></i>Tap to assign
                                     </div>
                                   )}
                                   {!isPending && allDaySchedules.length > 0 && !isAssigned && (
                                     <div className="text-muted mt-1" style={{ fontSize: '0.6rem' }}>
                                       {allDaySchedules.length} staff scheduled
                                     </div>
                                   )}
                                 </div>
                               </div>
                             );
                           })}
                        </div>
                      )}

                      {/* Month Picker */}
                      {viewMode === 'month' && (
                        <div>
                          <div className="row text-center mb-1">
                            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => (
                              <div key={d} className="col p-1 font-weight-bold text-muted" style={{ fontSize: '0.75rem', width: '14.28%', flex: '0 0 14.28%', maxWidth: '14.28%' }}>{d}</div>
                            ))}
                          </div>
                          {Array.from({ length: 6 }).map((_, weekIdx) => (
                            <div key={weekIdx} className="row">
                              {getPickerMonthDates().slice(weekIdx * 7, (weekIdx + 1) * 7).map((date, dayIdx) => {
                                const iso = formatDateISO(date);
                                const empSchedules = schedules.filter(s => s.employeeId === formData.employeeId && s.date === iso);
                                const allDaySchedules = schedules.filter(s => s.date === iso);
                                 const isAssigned = empSchedules.length > 0;
                                 const isCurrentDay = isToday(date);
                                 const inMonth = isPickerCurrentMonth(date);
                                 const isPending = pendingDays.has(iso);

                                 return (
                                   <div key={dayIdx} className="col p-1" style={{ width: '14.28%', flex: '0 0 14.28%', maxWidth: '14.28%' }}>
                                     <div
                                       className="text-center p-1"
                                       style={{
                                         borderRadius: 8,
                                         border: `2px solid ${isPending ? '#ff6b35' : isAssigned ? (empSchedules[0].shiftColor || '#28a745') : isCurrentDay ? '#ff6b35' : '#f0f0f0'}`,
                                         backgroundColor: isPending ? '#ff6b3508' : isAssigned ? (empSchedules[0].shiftColor || '#28a745') + '15' : isCurrentDay ? '#ff6b3508' : '#fff',
                                         cursor: isPending ? 'wait' : (inMonth ? 'pointer' : 'default'),
                                         minHeight: 50,
                                         opacity: inMonth ? 1 : 0.4,
                                       }}
                                       onClick={() => inMonth && !isPending && handlePickerDayClick(date)}
                                     >
                                       <div style={{
                                         fontSize: '0.8rem',
                                         fontWeight: isCurrentDay ? 'bold' : 'normal',
                                         color: isCurrentDay ? '#ff6b35' : inMonth ? '#333' : '#aaa',
                                       }}>
                                         {date.getDate()}
                                       </div>
                                       {isPending && (
                                         <div><i className="fas fa-spinner fa-spin" style={{ color: '#ff6b35', fontSize: '0.7rem' }}></i></div>
                                       )}
                                       {!isPending && isAssigned && (
                                         <div
                                           style={{
                                             width: 8,
                                             height: 8,
                                             borderRadius: '50%',
                                             backgroundColor: empSchedules[0].shiftColor || '#28a745',
                                             margin: '2px auto',
                                           }}
                                         />
                                       )}
                                       {!isPending && allDaySchedules.length > 0 && !isAssigned && inMonth && (
                                         <div className="text-muted" style={{ fontSize: '0.55rem' }}>{allDaySchedules.length}</div>
                                       )}
                                     </div>
                                   </div>
                                );
                              })}
                            </div>
                          ))}
                        </div>
                      )}

                      {!selectedEmployee && <p className="text-muted text-center mt-3">Select an employee to see available days</p>}
                      {selectedEmployee && !selectedShift && <p className="text-muted text-center mt-3">Select a shift to assign</p>}
                    </div>
                  )}

                  {/* RANGE MODE: Start/End Date */}
                  {modalMode === 'range' && (
                    <>
                      <div className="row">
                        <div className="col-6">
                          <div className="form-group">
                            <label>Start Date <span className="text-danger">*</span></label>
                            <input
                              type="date"
                              className="form-control"
                              value={formData.startDate}
                              onChange={e => setFormData({ ...formData, startDate: e.target.value })}
                            />
                          </div>
                        </div>
                        <div className="col-6">
                          <div className="form-group">
                            <label>End Date <span className="text-danger">*</span></label>
                            <input
                              type="date"
                              className="form-control"
                              value={formData.endDate}
                              onChange={e => setFormData({ ...formData, endDate: e.target.value })}
                            />
                          </div>
                        </div>
                      </div>
                      <div className="form-group">
                        <label>Status</label>
                        <select
                          className="form-control"
                          value={formData.status}
                          onChange={e => setFormData({ ...formData, status: e.target.value })}
                        >
                          {STATUS_OPTIONS.map(s => (
                            <option key={s.key} value={s.key}>{s.label}</option>
                          ))}
                        </select>
                      </div>
                      <div className="form-group">
                        <label>Notes</label>
                        <textarea
                          className="form-control"
                          rows={2}
                          value={formData.notes}
                          onChange={e => setFormData({ ...formData, notes: e.target.value })}
                          placeholder="Optional notes"
                        />
                      </div>
                    </>
                  )}

                  {/* EDIT MODE */}
                  {modalMode === 'edit' && (
                    <>
                      <div className="form-group">
                        <label>Date <span className="text-danger">*</span></label>
                        <input
                          type="date"
                          className="form-control"
                          value={formData.date}
                          onChange={e => setFormData({ ...formData, date: e.target.value })}
                        />
                      </div>
                      <div className="form-group">
                        <label>Status</label>
                        <select
                          className="form-control"
                          value={formData.status}
                          onChange={e => setFormData({ ...formData, status: e.target.value })}
                        >
                          {STATUS_OPTIONS.map(s => (
                            <option key={s.key} value={s.key}>{s.label}</option>
                          ))}
                        </select>
                      </div>
                      <div className="form-group">
                        <label>Notes</label>
                        <textarea
                          className="form-control"
                          rows={2}
                          value={formData.notes}
                          onChange={e => setFormData({ ...formData, notes: e.target.value })}
                          placeholder="Optional notes"
                        />
                      </div>
                    </>
                  )}
                </div>

                <div className="modal-footer border-0">
                  <button className="btn btn-outline-secondary" style={{ borderRadius: 8 }} onClick={() => setShowModal(false)}>
                    Cancel
                  </button>
                  {modalMode === 'range' && (
                    <button className="btn btn-primary" style={{ backgroundColor: '#ff6b35', borderColor: '#ff6b35', borderRadius: 8 }} onClick={handleRangeAssign}>
                      Assign All Days
                    </button>
                  )}
                  {modalMode === 'edit' && (
                    <button className="btn btn-primary" style={{ backgroundColor: '#ff6b35', borderColor: '#ff6b35', borderRadius: 8 }} onClick={handleSaveEdit}>
                      Update
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
    </SetupGuard>
  );

  if (embedded) return content;
  return (
    <AdminLayout title="Schedule">
      {content}
    </AdminLayout>
  );
};

export default SchedulePage;
